import { memo, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { FaEquals, FaPlus, FaPlusCircle, FaShoppingCart } from 'react-icons/fa';

import { IMainProduct } from '@/store/ducks/combos/types';

import ComboProduct from './ComboProduct';
import { Total } from './Total';

const ICON_SIZES = {
  SMALL: 15,
  MEDIUM: 22,
} as const;

const COLORS = {
  PRIMARY: '#007BFF',
  MUTED: '#8E8E8E',
  WHITE: 'white',
} as const;

interface ComboProductsProps {
  products: IMainProduct[];
  comboInfo: {
    combo_percentage?: number;
    combo_price: number;
    products_total?: number;
  };
  device: 'mobile' | 'desktop';
  openComboDetails?: () => void;
}

const ComboProducts = memo(
  ({ products, comboInfo, device, openComboDetails }: ComboProductsProps) => {
    const renderSeparatorIcon = useCallback(
      (index: number, arrayLength: number) => {
        if (index >= arrayLength - 1) return null;

        const iconProps = {
          size: ICON_SIZES.MEDIUM,
          className: 'combo-icon',
        };

        if (device === 'mobile') {
          return (
            <span key={`separator-${index}`} className="combo-icon">
              <FaPlusCircle {...iconProps} color={COLORS.PRIMARY} />
            </span>
          );
        }

        return (
          <span key={`separator-${index}`} className="combo-icon">
            <FaPlus {...iconProps} color={COLORS.MUTED} />
          </span>
        );
      },
      [device],
    );

    const renderEqualsIcon = useCallback(() => {
      if (device !== 'desktop') return null;

      return (
        <span className="combo-icon">
          <FaEquals color={COLORS.MUTED} size={ICON_SIZES.MEDIUM} />
        </span>
      );
    }, [device]);

    const containerClasses = `combo-products flex-lg-row flex-column pt-4 ${
      device === 'desktop' ? 'gap-2' : ''
    }`;

    return (
      <div className={containerClasses}>
        {products.map(({ min_quantity, full_product, variations_count }, index) => {
          const productToShow = {
            ...full_product,
            ...full_product.product,
            title:
              full_product.product?.full_product?.title || full_product.product?.title || 'Product',
          };

          return (
            <div key={`combo-product-${full_product.id || index}`} className="combo-item-wrapper">
              <ComboProduct
                product={productToShow}
                min_quantity={min_quantity}
                discount_percentage={comboInfo.combo_percentage || 0}
                variations_count={variations_count}
              />
              {renderSeparatorIcon(index, products.length)}
            </div>
          );
        })}

        {renderEqualsIcon()}

        <div className={`total-combo-${device}`}>
          <Total value={comboInfo.combo_price} products_total={comboInfo.products_total} />
          <Button variant="primary" className="w-100" onClick={openComboDetails}>
            <FaShoppingCart color={COLORS.WHITE} size={ICON_SIZES.SMALL} />
            {' Adicionar ao carrinho'}
          </Button>
        </div>
      </div>
    );
  },
);

ComboProducts.displayName = 'ComboProducts';

export default ComboProducts;
